import {
  DataFormat,
  TabularParser,
  JSONParser,
  DataSetLogic,
  DataSourceLogic,
  DataSourceType,
  SourceType,
  DataSet,
  LoadingJobLogic,
  LoadingJobData,
  JSONParsingOptions,
  TabularParsingOptions,
  DataFieldSchema,
  LoadToVertexData,
  LoadToEdgeData,
  LoadToVertexLogic,
  LoadToEdgeLogic,
  LoadingMappingLogic,
  GSQLLoadingJobJson,
  DBLoadingJobJson,
  GSQLDataSourceLogic,
  OneColumnMappingDataSourceColumnSource,
  OneColumnMappingMappingWidgetSource,
  OneColumnMappingGraphEntityTarget,
  OneColumnMappingMappingWidgetTarget,
  OneColumnMappingTargetType,
  LoadToVectorData,
  LoadToEntityLogic,
} from '@tigergraph/tools-models/loading-job';
import { SampleData } from '@/pages/ingestion/loadData/configureFile/DataSourceFormat';
import { Edge, Graph, Vertex, GSQLGraphJson } from '@tigergraph/tools-models/topology';
import { FormatValidator, HelperFunctions } from '@tigergraph/tools-models/utils';
import { StartLoadingJobPayload } from '@/pages/ingestion/loadData/type';
import cloneDeep from 'lodash-es/cloneDeep';
import { GSQLLoadingJobLogic } from '@tigergraph/tools-models/loading-job/gsql-loading-job-logic.model';
import { compare } from 'compare-versions';
import { ExternalGraph, ExternalLink, ExternalNode } from '@tigergraph/tools-models/gvis';
import { getLinkID } from '@tigergraph/tools-models';

export const dataSourceNodeType = ['gs_file', 'gs_url', 'gs_s3', 'gs_gcs', 'gs_abs', 'gs_snowflake'];
export const dataSourceEdgeType = 'gs_data_source_map';
export const auxNodeType = '__aux_node';

export const defaultParsingOptions = {
  format: '',
  separator: ',',
  eol: '\\n',
  header: true,
  quote: '',
};

export const createParsingOptions = (
  dataFormat: DataFormat,
  dataSourceType: DataSourceType,
  dataParser: TabularParser | JSONParser
): TabularParsingOptions | JSONParsingOptions | undefined => {
  let parsingOptions;
  switch (dataFormat) {
    case DataFormat.CSV:
    case DataFormat.TSV:
      parsingOptions =
        dataSourceType === DataSourceType.File
          ? DataSetLogic.createLocalFileSampleDataTabularParsingOptions(dataParser as TabularParser)
          : DataSetLogic.createTabularParsingOptions(dataParser as TabularParser);
      break;
    case DataFormat.JSON:
      parsingOptions = DataSetLogic.createJSONParsingOptions(dataParser as JSONParser);
      break;
    default:
      // TODO: Support more data formats in the future.
      break;
  }
  return parsingOptions;
};

export const createDataSchema = (sampleData: SampleData, dataFormat: DataFormat): DataFieldSchema[] | undefined => {
  if (!sampleData) {
    return;
  }
  if (dataFormat === DataFormat.CSV || dataFormat === DataFormat.TSV) {
    let header = sampleData.header;
    if (!header) {
      header = sampleData.data[0].map((_, index) => {
        return 'column_' + (index + 1);
      });
    }
    return DataSetLogic.createTabularDataSchema(header);
  } else if (dataFormat === DataFormat.JSON) {
    const standardJSONSchema = DataSetLogic.convertSampleDataToStandardSchema(sampleData.data);
    try {
      return DataSetLogic.convertStandardSchemaToInMemorySchema(standardJSONSchema);
    } catch (e) {
      console.error('JSON schema is incorrect. Please check the JSON schema.');
      return [];
    }
  }
};

export const createDataSet = (
  dataFormat: DataFormat,
  dataSchema: DataFieldSchema[] | undefined,
  dataSourceURI: string,
  parsingOptions: TabularParsingOptions | JSONParsingOptions | undefined,
  sampleData: SampleData
) => {
  if (!parsingOptions || !dataSchema) {
    return;
  }

  // Create a data file.
  let dataSet: DataSet;
  switch (dataFormat) {
    case DataFormat.CSV:
    case DataFormat.TSV:
      dataSet = {
        uri: dataSourceURI,
        parsingOptions: {
          ...parsingOptions,
          separator:
            DataSourceLogic.separators.find((sep) => sep[0] === parsingOptions.separator)?.[1] ??
            (parsingOptions as TabularParsingOptions).separator,
          eol: DataSourceLogic.eols.find((eol) => eol[0] === parsingOptions.eol)?.[1] || parsingOptions.eol,
          quote:
            DataSourceLogic.quotes.find((quote) => quote[0] === parsingOptions.quote)?.[1] ||
            parsingOptions.quote ||
            '',
        },
        dataFormat: dataFormat,
        dataSchema: dataSchema,
        sampleData: sampleData.data.slice(0, 10),
        position: { x: 0, y: 0 },
      };
      return dataSet;
    case DataFormat.JSON:
      dataSet = {
        uri: dataSourceURI,
        parsingOptions: parsingOptions,
        dataFormat: dataFormat,
        dataSchema: dataSchema,
        sampleData: sampleData.data.slice(0, 10),
        position: { x: 0, y: 0 },
      };
      return dataSet;
    default:
      // TODO: Support more data formats in the future.
      break;
  }
};

export const createLoadingJob = (
  dataFormat: DataFormat,
  dataSourceType: DataSourceType,
  dataParser: TabularParser | JSONParser,
  sampleData: SampleData,
  dataSourceName: string,
  dataSourceURI: string,
  loadingStatements: (LoadToVertexData | LoadToEdgeData)[]
): LoadingJobData | undefined => {
  const parsingOptions = createParsingOptions(dataFormat, dataSourceType, dataParser);
  const dataSchema = createDataSchema(sampleData, dataFormat);
  const dataSource = DataSourceLogic.createDataSource(dataSourceName, dataSourceType);
  const dataSet = createDataSet(dataFormat, dataSchema, dataSourceURI, parsingOptions, sampleData);
  if (!parsingOptions || !dataSchema || !dataSource || !dataSet) {
    return;
  }
  const jobName = LoadingJobLogic.createLoadingJobName(dataSource.type, dataSet.uri);
  const newLoadingJob = LoadingJobLogic.createLoadingJob(jobName, dataSource, dataSet, loadingStatements);

  return newLoadingJob;
};

export const createUsingClauses = (
  dataFormat: DataFormat,
  parsingOptions: TabularParsingOptions | JSONParsingOptions | undefined
): { [propName: string]: string } => {
  const usingClauses: { [propName: string]: string } = {};
  if (!parsingOptions) {
    return usingClauses;
  }
  usingClauses['EOL'] = DataSourceLogic.eols.find((eol) => eol[0] === parsingOptions.eol)?.[1] || parsingOptions.eol;

  switch (dataFormat) {
    case DataFormat.CSV:
    case DataFormat.TSV:
      usingClauses['SEPARATOR'] =
        DataSourceLogic.separators.find((sep) => sep[0] === parsingOptions.separator)?.[1] ??
        (parsingOptions as TabularParsingOptions).separator;
      usingClauses['HEADER'] = parsingOptions.header ? 'true' : 'false';
      usingClauses['EOL'] =
        DataSourceLogic.eols.find((eol) => eol[0] === parsingOptions.eol)?.[1] || parsingOptions.eol;
      if (parsingOptions.quote && parsingOptions.quote !== '') {
        usingClauses['QUOTE'] =
          DataSourceLogic.quotes.find((quote) => quote[0] === parsingOptions.quote)?.[1] ??
          ((parsingOptions as TabularParsingOptions).quote || 'none');
      }
      if (parsingOptions.separator === '\\u0020') {
        usingClauses['SEPARATOR'] = ' ';
      }
      break;
    case DataFormat.JSON:
      usingClauses['JSON_FILE'] = 'true';
      break;
  }

  // FIXME: special case for whitespace for now.
  if (parsingOptions.eol === '\\u0020') {
    usingClauses['EOL'] = ' ';
  }
  return usingClauses;
};

export const createLoadingStatement = (
  target: Vertex | Edge,
  usingClauses: { [propName: string]: string },
  sourceAndTarget?: { source: Vertex; target: Vertex }
): LoadToVertexData | LoadToEdgeData | undefined => {
  if ('primaryId' in target) {
    const loadToVertex = LoadToVertexLogic.createLoadToVertex(target, usingClauses);
    return loadToVertex;
  } else if (sourceAndTarget) {
    const loadToEdge = LoadToEdgeLogic.createLoadToEdge(
      target,
      sourceAndTarget?.source.name,
      sourceAndTarget?.target.name,
      usingClauses
    );
    return loadToEdge;
  }
};

const handleAddLoadingStatementMapping = (
  loadingStatement: LoadToVertexData | LoadToEdgeData,
  count = 1
): LoadToVertexData | LoadToEdgeData => {
  const tempLoadingStatement = { ...loadingStatement, mappings: [...loadingStatement.mappings] };
  for (let i = 0; i < count; i++) {
    tempLoadingStatement.mappings.push({
      // @ts-ignore
      sourceType: SourceType.Default,
    });
  }
  return tempLoadingStatement;
};

export const handleChangeLoadingStatementMapping = (
  loadingStatement: LoadToVertexData | LoadToEdgeData,
  mappings: {
    mappingSource: OneColumnMappingDataSourceColumnSource | OneColumnMappingMappingWidgetSource;
    mappingTarget: OneColumnMappingGraphEntityTarget | OneColumnMappingMappingWidgetTarget;
  }[],
  graph: Graph,
  dataFormat: DataFormat,
  dataSchema: DataFieldSchema[],
  inputLiteral: string = ''
): LoadToVertexData | LoadToEdgeData | LoadToVectorData => {
  let tempLoadingStatement = { ...loadingStatement, mappings: [...loadingStatement.mappings] };
  mappings.forEach(({ mappingSource, mappingTarget }) => {
    // @ts-ignore
    if (mappingSource.type === SourceType.Literal) {
      // @ts-ignore
      if (mappingTarget.type === OneColumnMappingTargetType.GraphEntity) {
        const mappingIndex = (mappingTarget as OneColumnMappingGraphEntityTarget).mappingIndex;
        let attributeType;
        let attributeValueType;
        if (LoadToEntityLogic.isLoadToVertexStatement(tempLoadingStatement) && mappingIndex > 0) {
          attributeType = graph.getVertex((tempLoadingStatement as LoadToVertexData).vertexName).attributes[
            mappingIndex - 1
          ].type.name;
          attributeValueType = graph.getVertex((tempLoadingStatement as LoadToVertexData).vertexName).attributes[
            mappingIndex - 1
          ].type.valueTypeName;
        } else if (LoadToEntityLogic.isLoadToEdgeStatement(tempLoadingStatement) && mappingIndex > 1) {
          attributeType = graph.getEdge((tempLoadingStatement as LoadToEdgeData).edgeName).attributes[mappingIndex - 2]
            .type.name;
          attributeValueType = graph.getEdge((tempLoadingStatement as LoadToEdgeData).edgeName).attributes[
            mappingIndex - 2
          ].type.valueTypeName;
        }
        if (attributeType === 'MAP') {
          throw new Error(`Currently you cannot map data to MAP attributes.`);
        }
        if (attributeType === 'UDT') {
          throw new Error(`Currently you cannot map data to UDT attributes.`);
        }
        if (attributeValueType === 'UDT') {
          throw new Error(`Currently you cannot map data to UDT value attributes.`);
        }
      }

      // Mapping
      // @ts-ignore
      if (mappingTarget.type === OneColumnMappingTargetType.GraphEntity) {
        LoadingMappingLogic.mapStringLiteralToEntity(
          tempLoadingStatement,
          (mappingTarget as OneColumnMappingGraphEntityTarget).mappingIndex,
          inputLiteral
        );
      } else {
        LoadingMappingLogic.mapStringLiteralToMappingWidget(
          tempLoadingStatement,
          (mappingTarget as OneColumnMappingMappingWidgetTarget).mappingWidgetIndex,
          (mappingTarget as OneColumnMappingMappingWidgetTarget).paramIndex,
          inputLiteral
        );
      }
      // @ts-ignore
    } else if (mappingSource.type === SourceType.DataSourceColumn) {
      const { columnIndex, isSplit, splitSeparator, splitKvSeparator } =
        mappingSource as OneColumnMappingDataSourceColumnSource;
      // @ts-ignore
      if (mappingTarget.type === OneColumnMappingTargetType.GraphEntity) {
        const mappingIndex = (mappingTarget as OneColumnMappingGraphEntityTarget).mappingIndex;
        while (mappingIndex >= tempLoadingStatement.mappings.length) {
          tempLoadingStatement = handleAddLoadingStatementMapping(tempLoadingStatement);
        }
        if (columnIndex < 0) {
          // @ts-ignore
          tempLoadingStatement.mappings[mappingIndex] = { sourceType: SourceType.Default };
        } else {
          LoadingMappingLogic.mapDataSourceToEntity(
            tempLoadingStatement,
            dataFormat,
            dataSchema,
            columnIndex,
            mappingIndex,
            graph,
            isSplit,
            splitSeparator,
            splitKvSeparator
          );
        }
      } else {
        const mappingWidgetIndex = (mappingTarget as OneColumnMappingMappingWidgetTarget).mappingWidgetIndex;
        const paramIndex = (mappingTarget as OneColumnMappingMappingWidgetTarget).paramIndex;
        LoadingMappingLogic.mapDataSourceToMappingWidget(
          tempLoadingStatement,
          dataFormat,
          dataSchema,
          columnIndex,
          mappingWidgetIndex,
          paramIndex
        );
      }
    } else {
      // @ts-ignore
      if (mappingTarget.type === OneColumnMappingTargetType.GraphEntity) {
        const mappingWidgetIndex = (mappingSource as OneColumnMappingMappingWidgetSource).mappingWidgetIndex;
        const mappingIndex = (mappingTarget as OneColumnMappingGraphEntityTarget).mappingIndex;
        while (mappingIndex >= tempLoadingStatement.mappings.length) {
          tempLoadingStatement = handleAddLoadingStatementMapping(tempLoadingStatement);
        }
        LoadingMappingLogic.mapMappingWidgetToEntity(tempLoadingStatement, mappingWidgetIndex, mappingIndex, graph);
      } else {
        const sourceMappingWidgetIndex = (mappingSource as OneColumnMappingMappingWidgetSource).mappingWidgetIndex;
        const targetMappingWidgetIndex = (mappingTarget as OneColumnMappingMappingWidgetTarget).mappingWidgetIndex;
        const paramIndex = (mappingTarget as OneColumnMappingMappingWidgetTarget).paramIndex;
        LoadingMappingLogic.mapMappingWidgetToMappingWidget(
          tempLoadingStatement,
          sourceMappingWidgetIndex,
          targetMappingWidgetIndex,
          paramIndex
        );
      }
    }
  });
  return tempLoadingStatement;
};

export const getStartingLoadingJobPayload = (
  loadingJobs: GSQLLoadingJobJson[],
  loadingJobsInfo: DBLoadingJobJson[],
  tgVersion = ''
): StartLoadingJobPayload[] => {
  const payload: StartLoadingJobPayload[] = [];
  for (let i = 0; i < loadingJobs.length; i++) {
    const loadingJob = loadingJobs[i];
    // Find the same loading job name in loadingJobsInfo
    const loadingJobInfo = loadingJobsInfo.find((jobInfo) => jobInfo.loadingJobName === loadingJob.JobName);
    if (!loadingJob || !loadingJobInfo) {
      continue;
    }
    let loadingJobInfoUrl = loadingJobInfo.dataSourceJson.uri ?? '';
    const loadingJobInfoType = loadingJobInfo.dataSourceJson.type ?? '';
    const dataSourceName = loadingJobInfo.dataSourceJson.dataSourceName ?? '';
    let name = '';
    let config = {} as { [key: string]: string };
    switch (loadingJobInfoType) {
      case DataSourceType.File:
        name = 'file';
        break;
      default:
        name = dataSourceName;
        break;
    }
    switch (loadingJobInfoType) {
      case DataSourceType.Snowflake:
        config = {
          query: `SELECT * FROM ${loadingJobInfo.dataSourceJson.uri}`,
        };
        break;
      default:
        break;
    }
    payload.push({
      name: loadingJob?.JobName,
      streaming:
        tgVersion && compare(tgVersion, '3.9.2', '>=')
          ? GSQLDataSourceLogic.isKafkaLoadingJob(loadingJob?.JobName ?? '')
          : false,
      dataSources: [
        {
          filename: HelperFunctions.dataFileName,
          name,
          path: DataSetLogic.getFileUriForConnector(loadingJobInfoUrl, loadingJobInfoType),
          config,
        },
      ],
    });
  }
  return payload;
};

export const filterValidLoadingJobs = (graphSchema: Graph, loadingJobs: { [key: string]: LoadingJobData }) => {
  loadingJobs = cloneDeep(loadingJobs);
  const fileNames = Object.keys(loadingJobs);
  const vertexTypes = graphSchema.getAllVertexTypes();
  const edgeTypes = graphSchema.getAllEdgeTypes();
  for (let name of fileNames) {
    loadingJobs[name].loadingStatements = loadingJobs[name].loadingStatements.filter(
      // Filter out the loading statement with incorrect mappings
      (loadingStatement) => {
        if (LoadToEntityLogic.isLoadToVectorStatement(loadingStatement)) {
          return (
            vertexTypes.includes((loadingStatement as LoadToVectorData).vertexName) &&
            graphSchema
              .getVertex((loadingStatement as LoadToVectorData).vertexName)
              .embeddingAttributes.find(
                (attribute) => attribute.name === (loadingStatement as LoadToVectorData).vectorName
              ) &&
            loadingStatement.mappings.length === 2 &&
            // @ts-ignore
            loadingStatement.mappings[0].sourceType !== SourceType.Default &&
            // @ts-ignore
            loadingStatement.mappings[1].sourceType !== SourceType.Default &&
            loadingStatement.mappings[1].index !== undefined &&
            loadingStatement.mappings[1].index >= 0
          );
        } else if (LoadToEntityLogic.isLoadToVertexStatement(loadingStatement)) {
          return (
            vertexTypes.includes((loadingStatement as LoadToVertexData).vertexName) &&
            // @ts-ignore
            loadingStatement.mappings[0].sourceType !== SourceType.Default
          );
        } else if (LoadToEntityLogic.isLoadToEdgeStatement(loadingStatement)) {
          return (
            edgeTypes.includes((loadingStatement as LoadToEdgeData).edgeName) &&
            // @ts-ignore
            loadingStatement.mappings[0].sourceType !== SourceType.Default &&
            // @ts-ignore
            loadingStatement.mappings[1].sourceType !== SourceType.Default
          );
        }
      }
    );
    if (loadingJobs[name].loadingStatements.length === 0) {
      delete loadingJobs[name];
    }
  }
  return loadingJobs;
};

export function calculateLoadingJob(
  graphSchema: Graph,
  loadingJobs: { [key: string]: LoadingJobData },
  savedLoadingJobs: LoadingJobData[],
  savedGSQLLoadingJobs: GSQLLoadingJobJson[],
  tgVersion?: string
) {
  loadingJobs = filterValidLoadingJobs(graphSchema, loadingJobs);
  const gsqlLoadingJobsJson: GSQLLoadingJobJson[] = [];
  const dbLoadingJobsJson: DBLoadingJobJson[] = [];
  if (graphSchema) {
    [...Object.values(loadingJobs), ...savedLoadingJobs].forEach((loadingJob) => {
      delete loadingJob.isChanged;
      const [gsqlJson, dbJson] = LoadingJobLogic.dumpToGSQLAndDB(cloneDeep(loadingJob), graphSchema);
      if (gsqlJson !== undefined) {
        gsqlLoadingJobsJson.push(gsqlJson);
      }
      // Remove the empty mappingWidgetIndices
      dbJson.loadingStatementsStyle.forEach((loadingStatementStyle) => {
        if (loadingStatementStyle.mappingWidgetIndices?.length === 0) {
          delete loadingStatementStyle.mappingWidgetIndices;
        }
      });
      dbLoadingJobsJson.push(dbJson);
    });
  }
  const gsqlLoadingJobChange = GSQLLoadingJobLogic.getGSQLLoadingJobChange(
    savedGSQLLoadingJobs.sort((a, b) => a.JobName.localeCompare(b.JobName)),
    gsqlLoadingJobsJson.sort((a, b) => a.JobName.localeCompare(b.JobName))
  );

  const dataSources = getStartingLoadingJobPayload(
    gsqlLoadingJobChange.loadingJobsToAdd.filter((loadingJob) =>
      Object.values(loadingJobs).find((job) => job.jobName === loadingJob.JobName)
    ),
    dbLoadingJobsJson,
    tgVersion
  );

  return {
    gsqlLoadingJobChange,
    dbLoadingJobsJson,
    dataSources,
  };
}

export const getDataFormat = (dataSourceURI: string) => {
  if (dataSourceURI.endsWith('.csv')) {
    return DataFormat.CSV;
  } else if (dataSourceURI.endsWith('.tsv')) {
    return DataFormat.TSV;
  } else if (dataSourceURI.endsWith('.json') || dataSourceURI.endsWith('.jsonl')) {
    return DataFormat.JSON;
  }
  return DataFormat.CSV;
};

export const checkDataFormat = (data: string[][]) => {
  const rawData = data.map((row) => row.join()).join();
  return rawData.trim().charAt(0) === '{' ? DataFormat.JSON : DataFormat.CSV;
};

export const isValidRemoteUri = (dataSourceType: DataSourceType, uri: string) => {
  return uri.length === 0 || DataSetLogic.isValidRemoteUri(dataSourceType, uri);
};

export const isSupportedWarehouseDataSourceType = (dataSourceType: DataSourceType, tgVersion = ''): boolean => {
  if (tgVersion && compare(tgVersion, '4.1.x', '>') && dataSourceType === DataSourceType.Snowflake) {
    return true;
  }
  return false;
};

export const isSupportedOnlineDataSourceType = (dataSourceType: DataSourceType): boolean => {
  return (
    dataSourceType === DataSourceType.S3 ||
    dataSourceType === DataSourceType.GoogleCloudStorage ||
    dataSourceType === DataSourceType.AzureBlobStorage
  );
};

export const isSupportedDataSourceType = (dataSourceType: DataSourceType, tgVersion = ''): boolean => {
  return (
    dataSourceType === DataSourceType.File ||
    isSupportedOnlineDataSourceType(dataSourceType) ||
    isSupportedWarehouseDataSourceType(dataSourceType, tgVersion)
  );
};

export const validateDataSourceName = (name: string, allNames: string[]) => {
  if (!name) {
    return;
  }
  const validationResult = FormatValidator.isName(name);
  if (!validationResult.success) {
    return validationResult.message;
  }
  if (allNames.includes(name)) {
    return 'This name is already in the data source';
  }
  return;
};

export const filterValidDataSourceURIs = (dataSourceURIs: string[]) => {
  return dataSourceURIs.map((uri) => uri.trim().replace(/\/$/g, '')).filter((uri) => uri);
};

export const formatTabularSampleDataFromLocalFile = (data: SampleData): SampleData => {
  const sampleData = cloneDeep(data);
  // Find the maximum column number.
  let maxColNumber = sampleData.header.length;
  sampleData.data.forEach((row) => (maxColNumber = maxColNumber < row.length ? row.length : maxColNumber));

  // If the header is empty, add default header.
  for (let i = 0; i < maxColNumber; i++) {
    if (!sampleData.header[i]) {
      sampleData.header[i] = `column_${i + 1}`;
    }
  }

  // Filter out rows with empty data.
  sampleData.data = sampleData.data.filter((row) => row.length > 0 && !row.every((col) => col === ''));

  // Pad additional column for mismatch row.
  for (let i = 0; i < sampleData.data.length; i++) {
    const row = sampleData.data[i];
    const length = row.length;

    if (length < maxColNumber) {
      for (let j = 0; j < maxColNumber - length; j++) {
        row.push('N/A');
      }
    }
  }

  return sampleData;
};

export const addDataSourceAndMapping = (
  schema: GSQLGraphJson,
  loadingJobs: LoadingJobData[]
  // semanticCheckResults: { success: boolean; message: string[] }[]
) => {
  const graph: ExternalGraph = {
    nodes: [],
    links: [],
  };
  loadingJobs.forEach((loadingJob, i) => {
    const vertex: ExternalNode = {
      type: `gs_${loadingJob.dataSource.type}`,
      id: `${i}`,
      attrs: {},
      x: loadingJob.dataSet.position?.x ?? 0,
      y: loadingJob.dataSet.position?.y ?? 0,
      others: {
        items: [
          {
            text: DataSetLogic.createDataSetLabel(false, loadingJob.dataSet.uri),
          },
        ],
        isLoading: false,
        jobName: loadingJob.jobName,
        dataSource: loadingJob.dataSource,
        dataSet: loadingJob.dataSet,
        isChanged: loadingJob.isChanged,
      },
    };

    // Add file info for local file type loading job if file still exists on server.
    switch (loadingJob.dataSource.type) {
      case DataSourceType.File:
        if (loadingJob.dataSet.info) {
          vertex.attrs = {
            'Last Modified': HelperFunctions.epochToDatetime(loadingJob.dataSet.info.modTime),
            Size: HelperFunctions.abbreviateByte(loadingJob.dataSet.info.size, 1),
          };
        }
        break;
      default:
        break;
    }

    let addedEdge = false;
    loadingJob.loadingStatements.forEach((loadingStatement, j) => {
      // @ts-ignore
      if (loadingStatement.mappings.every((mapping) => mapping.sourceType === SourceType.Default)) {
        // Skip the loading statement if all mappings are default.
        return;
      }
      const edge: ExternalLink = {
        // Append statement index in case a loading has same target element.
        type: `${dataSourceEdgeType}_${j}`,
        source: {
          type: `gs_${loadingJob.dataSource.type}`,
          id: `${i}`,
        },
        target: {
          type: '',
          id: '',
        },
        others: {
          statementId: `${j}`,
        },
      };

      if (LoadToEntityLogic.isLoadToVertexStatement(loadingStatement)) {
        const statement = <LoadToVertexData>loadingStatement;
        if (schema.VertexTypes.find((vertex) => vertex.Name === statement.vertexName)) {
          edge.target.type = statement.vertexName;
          edge.target.id = statement.vertexName;
        }
      } else {
        const statement = <LoadToEdgeData>loadingStatement;
        if (
          schema.EdgeTypes.find((edge) => edge.Name === statement.edgeName) &&
          schema.VertexTypes.find((vertex) => vertex.Name === statement.fromVertexType) &&
          schema.VertexTypes.find((vertex) => vertex.Name === statement.toVertexType)
        ) {
          // Map to the virtual vertex instead of the edge.
          edge.target.type = auxNodeType;
          edge.target.id = getLinkID({
            type: statement.edgeName,
            source: {
              type: statement.fromVertexType,
              id: statement.fromVertexType,
            },
            target: {
              type: statement.toVertexType,
              id: statement.toVertexType,
            },
            directed: schema.EdgeTypes.find((edge) => edge.Name === statement.edgeName)?.IsDirected ?? false,
          });
        }
      }

      // Filter out loading statements that have vector name.
      if (!LoadToEntityLogic.isLoadToVectorStatement(loadingStatement) && edge.target.type && edge.target.id) {
        graph.links.push(edge);
        addedEdge = true;
      }
    });
    if (addedEdge) {
      graph.nodes.push(vertex);
    }
  });
  return graph;
};
